import React from 'react';
import { gql, useQuery } from '@common/hooks';
import {
  AlignedInfo,
  ErrorHandler,
  HistoryDropDown,
  LoadingState,
} from '@common/components';
import { humanizeTimezone } from 'works/legacy/lib/date-helper';
import { HistoryInfoFragment, HistoryInfoFragmentType } from '@common/graphql';

const orgUnitDetailsQuery = gql`
  query OrgUnitDetailsQuery($orgUnitId: ID!, $scope: String!) {
    organizationUnit(uuid: $orgUnitId, scope: $scope) {
      id
      name
      costCenter
      clinicalUnit {
        id
        displayName
      }
      organizationBusiness {
        id
        name
        timeZone
      }
      history {
        ...HistoryInfoFragment
      }
    }
  }
  ${HistoryInfoFragment}
`;

type orgUnitDetailsQueryResponse = {
  organizationUnit: {
    id: string;
    name: string;
    costCenter: string;
    clinicalUnit: {
      id: string;
      displayName: string;
    };
    organizationBusiness: {
      id: string;
      name: string;
      timeZone: string;
    };
    history: HistoryInfoFragmentType[];
  };
};

type orgUnitDetailsQueryVariables = {
  orgUnitId: string;
  scope: 'organization';
};

type Props = {
  organizationUnitId: string;
};

export const OrganizationUnitDetails = ({ organizationUnitId }: Props) => {
  // @TODO: find a way to solve Apollo re-rendering many times. The cache should be optional, and for this case is required.
  const { data, error, loading } = useQuery<
    orgUnitDetailsQueryResponse,
    orgUnitDetailsQueryVariables
  >(orgUnitDetailsQuery, {
    fetchPolicy: 'cache-first',
    variables: {
      orgUnitId: organizationUnitId,
      scope: 'organization',
    },
  });

  if (error) {
    return (
      <ErrorHandler error={error} log="AdminOrganizationUnitDetailsView" />
    );
  }

  if (loading) {
    return <LoadingState />;
  }

  const organizationUnit = data?.organizationUnit;
  const organizationUnitsDetailsContent = [
    ['Facility', organizationUnit?.organizationBusiness.name],
    ['Unit Type', organizationUnit?.clinicalUnit.displayName],
    [
      'Cost Center',
      organizationUnit?.costCenter || (
        <span className="color-midnight-lighter">N/A</span>
      ),
    ],
    [
      'Facility Time Zone',
      humanizeTimezone(organizationUnit?.organizationBusiness.timeZone),
    ],
  ];

  return (
    <>
      <div className="organization-unit-details">
        <AlignedInfo content={organizationUnitsDetailsContent} />
        <HistoryDropDown
          data={organizationUnit?.history || []}
          hrVisible={false}
        />
      </div>
      <style jsx>{`
        .organization-unit-details {
          padding: 24px 0;
        }
      `}</style>
    </>
  );
};
